import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageIcon, Save } from "lucide-react";

// Admin editor for the per-group default deal image (swap the picture without code).
export const CategoryImagesTab = () => {
  const [groups, setGroups] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/deals/category-defaults");
      setGroups(data);
    } catch (err) {
      console.debug("category defaults load failed:", err?.message);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (group) => {
    const url = (edits[group] ?? "").trim();
    if (!url) { toast.error("Paste an image URL first"); return; }
    setSavingKey(group);
    try {
      await api.put(`/admin/category-defaults/${group}`, { image_url: url });
      toast.success("Default image updated");
      setEdits((p) => ({ ...p, [group]: undefined }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><ImageIcon className="h-4 w-4" /></div>
        <div>
          <h3 className="font-heading text-lg" style={{ fontWeight: 700 }}>Default deal images</h3>
          <p className="text-xs text-muted-foreground">Any deal without its own picture shows its group's default. Paste a new image URL to swap it — no code needed.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => (
          <div key={g.group} data-testid={`catimg-${g.group}`} className="rounded-xl border border-black/10 bg-white overflow-hidden">
            <img src={edits[g.group] || g.image_url} alt={g.label}
              className="h-36 w-full object-cover bg-secondary"
              onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
            <div className="p-3">
              <p className="font-heading text-sm" style={{ fontWeight: 700 }}>{g.label}</p>
              <p className="text-[11px] text-muted-foreground">{(g.categories || []).join(", ")}</p>
              <div className="mt-2 flex gap-2">
                <Input
                  data-testid={`catimg-input-${g.group}`}
                  value={edits[g.group] ?? g.image_url}
                  onChange={(e) => setEdits((p) => ({ ...p, [g.group]: e.target.value }))}
                  placeholder="https://…/image.jpg"
                  className="h-9 text-xs"
                />
                <Button size="sm" data-testid={`catimg-save-${g.group}`} onClick={() => save(g.group)}
                  disabled={savingKey === g.group} className="h-9 rounded-full shrink-0">
                  <Save className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryImagesTab;
